import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiCalendar, FiUsers, FiSettings, FiLogOut } from 'react-icons/fi';
import { getBusinessPlan, getFeatureLabel, getRequiredPlanLabelForFeature, hasPlanFeature } from '../utils/planFeatures';
import './Navbar.css';

// Support contacts shown in the mobile help panel
const SUPPORT_PHONE_DISPLAY = '0585576176';
const SUPPORT_EMAIL_DISPLAY = 'itamar.fried1@gmail.com';
const LIGHT_LOGO_SRC = '/updated%20dark%20mode%20logo.png';
const DARK_LOGO_SRC = '/updated%20dark%20mode%20logo.png';

function Navbar({ user, onLogout, theme, onToggleTheme }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const mobileDrawerRef = useRef(null);
  const hamburgerButtonRef = useRef(null);
  const isDark = theme === 'dark';
  const noAppointmentsMode = Boolean(user?.no_appointments_mode || user?.settings?.no_appointments_mode);
  const businessPlan = getBusinessPlan(user);
  const isFreePlan = businessPlan.isFree;
  const headerLogoSrc = isDark ? DARK_LOGO_SRC : LIGHT_LOGO_SRC;
  const themeToggleLabel = theme === 'dark' ? '☀️ מצב בהיר' : '🌙 מצב כהה';

  const openPlanPicker = (featureKey) => {
    closeMobileMenu();
    navigate('/my-plan', { state: { upgradeFeature: featureKey, sourcePath: window.location.pathname } });
  };

  const renderLockedNavItem = (featureKey, className) => {
    const requiredPlanLabel = getRequiredPlanLabelForFeature(featureKey);
    return (
    <button
      type="button"
      className={`${className} ${className}--locked`}
      onClick={() => openPlanPicker(featureKey)}
      title={`${getFeatureLabel(featureKey)} זמין רק במסלול ${requiredPlanLabel}`}
    >
      <span>{getFeatureLabel(featureKey)}</span>
      <span className="nav-lock-badge">{requiredPlanLabel}</span>
    </button>
    );
  };

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  useEffect(() => {
    const drawer = mobileDrawerRef.current;
    if (!drawer) return;

    if (mobileMenuOpen) {
      drawer.removeAttribute('inert');
      return;
    }

    drawer.setAttribute('inert', '');
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setSupportOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const scrollY = window.scrollY;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };

    window.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => {
    try {
      const activeElement = document.activeElement;
      const drawer = mobileDrawerRef.current;
      if (drawer && activeElement && drawer.contains(activeElement) && typeof activeElement.blur === 'function') {
        activeElement.blur();
      }
    } catch {
      // ignore focus management failures
    }

    setMobileMenuOpen(false);
    setSupportOpen(false);

    window.setTimeout(() => {
      try {
        hamburgerButtonRef.current?.focus();
      } catch {
        // ignore focus restoration failures
      }
    }, 0);
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/appointments" className="navbar-brand">
          <img
            src={headerLogoSrc}
            alt="תן לי תור"
            className="brand-banner"
            width="2402"
            height="1760"
            decoding="async"
            fetchPriority="high"
          />
        </Link>

        <div className="navbar-menu">
          {hasPlanFeature(user, 'business-status') && noAppointmentsMode ? (
            <Link to="/business-status" className="nav-link">
              🟢 סטטוס העסק
            </Link>
          ) : null}
          {hasPlanFeature(user, 'appointments') ? (
            <Link to="/appointments" className="nav-link">
              📅 ניהול תורים
            </Link>
          ) : renderLockedNavItem('appointments', 'nav-link')}
          {hasPlanFeature(user, 'external-order-link') ? (
            <Link to="/my-site" className="nav-link">
              🌐 האתר שלי
            </Link>
          ) : renderLockedNavItem('external-order-link', 'nav-link')}
          {hasPlanFeature(user, 'external-order-link') ? (
            <Link to="/external-order" className="nav-link">
              🔗 הזמנה חיצונית
            </Link>
          ) : renderLockedNavItem('external-order-link', 'nav-link')}
          {hasPlanFeature(user, 'approvals') ? (
            <Link to="/approvals" className="nav-link">
              ⏳ מחכים לאישור
            </Link>
          ) : renderLockedNavItem('approvals', 'nav-link')}
          {(!noAppointmentsMode || isFreePlan) && (
            <>
              {hasPlanFeature(user, 'waitlist') ? (
                <Link to="/waitlist" className="nav-link">
                  📋 רשימת המתנה
                </Link>
              ) : renderLockedNavItem('waitlist', 'nav-link')}
              {hasPlanFeature(user, 'schedule') ? (
                <Link to="/schedule" className="nav-link">
                  <FiCalendar /> שעות
                </Link>
              ) : renderLockedNavItem('schedule', 'nav-link')}
              {hasPlanFeature(user, 'temp-closure') ? (
                <Link to="/temp-closure" className="nav-link">
                  🔒 פתיחה/סגירה זמנית
                </Link>
              ) : renderLockedNavItem('temp-closure', 'nav-link')}
              {hasPlanFeature(user, 'customers') ? (
                <Link to="/customers" className="nav-link">
                  👥 הלקוחות שלי
                </Link>
              ) : renderLockedNavItem('customers', 'nav-link')}
              {hasPlanFeature(user, 'services') ? (
                <Link to="/services" className="nav-link">
                  💇‍♂️ שירותים
                </Link>
              ) : renderLockedNavItem('services', 'nav-link')}
              {hasPlanFeature(user, 'analytics') ? (
                <Link to="/analytics" className="nav-link">
                  📈 ניתוח עסק
                </Link>
              ) : renderLockedNavItem('analytics', 'nav-link')}
              {hasPlanFeature(user, 'staff') ? (
                <Link to="/staff" className="nav-link">
                  <FiUsers /> צוות
                </Link>
              ) : renderLockedNavItem('staff', 'nav-link')}
            </>
          )}
          {hasPlanFeature(user, 'no-appointments-mode') ? (
            <Link to="/settings" className="nav-link">
              <FiSettings /> הגדרות
            </Link>
          ) : renderLockedNavItem('no-appointments-mode', 'nav-link')}
          <Link to="/my-plan" className="nav-link">
            📋 התוכנית שלי
          </Link>
        </div>

        <div className="navbar-right">
          <div className="user-info">
            <span className="user-name">{user?.name || user?.business_name || ''}</span>
          </div>

          {!mobileMenuOpen && (
            <>
              <button
                ref={hamburgerButtonRef}
                className="navbar-hamburger"
                onClick={() => setMobileMenuOpen(true)}
                type="button"
                aria-label="פתח תפריט"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-nav-drawer"
              >
                ☰
              </button>
            </>
          )}

          <button className="logout-btn" onClick={handleLogout}>
            <FiLogOut /> התנתק
          </button>
        </div>
      </div>

      <div
        className={mobileMenuOpen ? 'mobile-nav-overlay open' : 'mobile-nav-overlay'}
        role="presentation"
        onClick={closeMobileMenu}
      />

      <aside
        ref={mobileDrawerRef}
        id="mobile-nav-drawer"
        className={mobileMenuOpen ? 'mobile-nav-drawer open' : 'mobile-nav-drawer'}
        aria-hidden={!mobileMenuOpen}
      >
        <div className="mobile-nav-header">
          <div className="mobile-nav-title">תפריט</div>
          <button className="mobile-nav-close" type="button" onClick={closeMobileMenu} aria-label="סגור">
            ✕
          </button>
        </div>

        <nav className="mobile-nav-links" aria-label="ניווט">
          {hasPlanFeature(user, 'business-status') && noAppointmentsMode ? (
            <Link to="/business-status" className="mobile-nav-link" onClick={closeMobileMenu}>
              🟢 סטטוס העסק
            </Link>
          ) : null}
          {hasPlanFeature(user, 'appointments') ? (
            <Link to="/appointments" className="mobile-nav-link" onClick={closeMobileMenu}>
              📅 ניהול תורים
            </Link>
          ) : renderLockedNavItem('appointments', 'mobile-nav-link')}
          {hasPlanFeature(user, 'external-order-link') ? (
            <Link to="/my-site" className="mobile-nav-link" onClick={closeMobileMenu}>
              🌐 האתר שלי
            </Link>
          ) : renderLockedNavItem('external-order-link', 'mobile-nav-link')}
          {hasPlanFeature(user, 'external-order-link') ? (
            <Link to="/external-order" className="mobile-nav-link" onClick={closeMobileMenu}>
              🔗 הזמנה חיצונית
            </Link>
          ) : renderLockedNavItem('external-order-link', 'mobile-nav-link')}
          {hasPlanFeature(user, 'approvals') ? (
            <Link to="/approvals" className="mobile-nav-link" onClick={closeMobileMenu}>
              ⏳ מחכים לאישור
            </Link>
          ) : renderLockedNavItem('approvals', 'mobile-nav-link')}
          {(!noAppointmentsMode || isFreePlan) && (
            <>
              {hasPlanFeature(user, 'waitlist') ? (
                <Link to="/waitlist" className="mobile-nav-link" onClick={closeMobileMenu}>
                  📋 רשימת המתנה
                </Link>
              ) : renderLockedNavItem('waitlist', 'mobile-nav-link')}
              {hasPlanFeature(user, 'schedule') ? (
                <Link to="/schedule" className="mobile-nav-link" onClick={closeMobileMenu}>
                  <FiCalendar /> שעות
                </Link>
              ) : renderLockedNavItem('schedule', 'mobile-nav-link')}
              {hasPlanFeature(user, 'temp-closure') ? (
                <Link to="/temp-closure" className="mobile-nav-link" onClick={closeMobileMenu}>
                  🔒 פתיחה/סגירה זמנית
                </Link>
              ) : renderLockedNavItem('temp-closure', 'mobile-nav-link')}
              {hasPlanFeature(user, 'customers') ? (
                <Link to="/customers" className="mobile-nav-link" onClick={closeMobileMenu}>
                  👥 הלקוחות שלי
                </Link>
              ) : renderLockedNavItem('customers', 'mobile-nav-link')}
              {hasPlanFeature(user, 'services') ? (
                <Link to="/services" className="mobile-nav-link" onClick={closeMobileMenu}>
                  💇‍♂️ שירותים
                </Link>
              ) : renderLockedNavItem('services', 'mobile-nav-link')}
              {hasPlanFeature(user, 'analytics') ? (
                <Link to="/analytics" className="mobile-nav-link" onClick={closeMobileMenu}>
                  📈 ניתוח עסק
                </Link>
              ) : renderLockedNavItem('analytics', 'mobile-nav-link')}
              {hasPlanFeature(user, 'staff') ? (
                <Link to="/staff" className="mobile-nav-link" onClick={closeMobileMenu}>
                  <FiUsers /> צוות
                </Link>
              ) : renderLockedNavItem('staff', 'mobile-nav-link')}
            </>
          )}
          {hasPlanFeature(user, 'no-appointments-mode') ? (
            <Link to="/settings" className="mobile-nav-link" onClick={closeMobileMenu}>
              <FiSettings /> הגדרות
            </Link>
          ) : renderLockedNavItem('no-appointments-mode', 'mobile-nav-link')}
          <Link to="/my-plan" className="mobile-nav-link" onClick={closeMobileMenu}>
            📋 התוכנית שלי
          </Link>
          <Link to="/help" className="mobile-nav-link" onClick={closeMobileMenu}>
            ❓ עזרה ומדריך שימוש
          </Link>

          <button
            type="button"
            className="mobile-nav-link mobile-nav-link--theme"
            onClick={onToggleTheme}
            aria-label={themeToggleLabel}
            title={themeToggleLabel}
          >
            {themeToggleLabel}
          </button>

          <div className={supportOpen ? 'mobile-help open' : 'mobile-help'}>
            <button
              type="button"
              className="mobile-help-toggle"
              onClick={() => setSupportOpen(v => !v)}
              aria-expanded={supportOpen}
            >
              🆘 עזרה ותמיכה
            </button>

            {supportOpen && (
              <div className="mobile-help-body">
                <div className="mobile-help-header">
                  <button
                    type="button"
                    className="mobile-help-close"
                    onClick={() => setSupportOpen(false)}
                    aria-label="סגור"
                  >
                    ✕
                  </button>
                  <div className="mobile-help-title">פרטי תמיכה</div>
                </div>

                <div className="mobile-help-text">
                  היי! אני איתמר 🤚
                  <br />
                  אני כאן לכל מה שאתם צריכים — שאלות, תקלות, רעיונות ושדרוגים.
                  <br />
                  גם אם בא לכם לשפר משהו במערכת או להוסיף פיצ׳ר חדש — יש לנו צוות מפתחים שיכול לעזור ולהרים את זה מהר.
                  <br />
                  תרגישו חופשי לפנות אליי:
                </div>

                <div className="mobile-help-link">טלפון: {SUPPORT_PHONE_DISPLAY}</div>
                <div className="mobile-help-link">Gmail: {SUPPORT_EMAIL_DISPLAY}</div>
              </div>
            )}
          </div>

          <button className="mobile-nav-link danger" onClick={() => { closeMobileMenu(); handleLogout(); }} type="button">
            <FiLogOut /> התנתק
          </button>
        </nav>
      </aside>
    </nav>
  );
}

export default Navbar;
